import React, { useState, useEffect } from 'react';
import { clinicService } from '../../services/api';

/**
 * A component for displaying evaluation results for a clinic
 *
 * @param {Object} props
 * @param {string} props.clinicId - The clinic ID
 * @param {Function} props.onDataChange - Callback function when data changes
 */
const EvaluationSection = ({ clinicId, onDataChange }) => {
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(20); // 20 records per page
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track expanded rows
  const [expandedDetails, setExpandedDetails] = useState(new Map()); // Store loaded details
  const [loadingDetails, setLoadingDetails] = useState(new Set()); // Track loading IDs

  const MAX_EXPANDED_RECORDS = 50; // Maximum records to show in expanded view

  useEffect(() => {
    loadEvaluations();
  }, [clinicId, skip]);

  const loadEvaluations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await clinicService.getClinicEvaluations(clinicId, skip, limit);
      setEvaluations(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Error loading evaluations:', err);
      setError('Failed to load evaluations');
      setEvaluations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    // Reset expanded rows and details when refreshing
    setExpandedRows(new Set());
    setExpandedDetails(new Map());
    setLoadingDetails(new Set());
    // Reload evaluations
    loadEvaluations();
    if (onDataChange) {
      onDataChange();
    }
  };

  const handleDownloadResult = async (evaluationId) => {
    try {
      console.log(`Downloading evaluation result: ${evaluationId}`);
      await clinicService.downloadEvaluationResult(clinicId, evaluationId);
      console.log('Download completed successfully');
    } catch (err) {
      console.error('Error downloading evaluation result:', err);
      setError('Failed to download evaluation result');
    }
  };

  const toggleRowExpansion = async (evaluationId) => {
    const newExpanded = new Set(expandedRows);

    if (newExpanded.has(evaluationId)) {
      // Collapse
      newExpanded.delete(evaluationId);
    } else {
      // Expand - load details on demand
      newExpanded.add(evaluationId);

      // If not loaded yet, load it
      if (!expandedDetails.has(evaluationId)) {
        setLoadingDetails(prev => new Set(prev).add(evaluationId));
        try {
          const detail = await clinicService.getEvaluationDetail(clinicId, evaluationId);
          setExpandedDetails(prev => new Map(prev).set(evaluationId, detail));
        } catch (err) {
          console.error('Error loading evaluation detail:', err);
          setError('Failed to load evaluation details');
          newExpanded.delete(evaluationId); // Don't expand if load failed
        } finally {
          setLoadingDetails(prev => {
            const next = new Set(prev);
            next.delete(evaluationId);
            return next;
          });
        }
      }
    }

    setExpandedRows(newExpanded);
  };

  const canExport = (evaluation) => {
    // Download is independent - it queries the database directly
    // So we can enable download for any evaluation that has a valid type
    // The backend will handle checking if data exists
    return evaluation.type === 'test_case_evaluation' ||
           evaluation.type === 'real_case_evaluation';
  };

  const getTestCaseDetails = (evaluation) => {
    // Get details from expandedDetails cache
    const detail = expandedDetails.get(evaluation.id);
    if (!detail || detail.type !== 'test_case_evaluation' || !detail.result) {
      return [];
    }

    const details_by_code = detail.result.details_by_code_dict || {};
    const rows = [];

    for (const [code, details] of Object.entries(details_by_code)) {
      const questions = details.list_of_question || [];
      const responses = details.list_of_response || [];
      const toolInfos = details.list_of_adjusted_tool_info || [];

      for (let idx = 0; idx < questions.length; idx++) {
        rows.push({
          code,
          question: questions[idx] || '',
          response: responses[idx] || '',
          tool_info: toolInfos[idx] ? (typeof toolInfos[idx] === 'string' ? toolInfos[idx] : JSON.stringify(toolInfos[idx])) : ''
        });
      }
    }

    return rows;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString();
    } catch (e) {
      return timestamp;
    }
  };

  const formatJSON = (obj) => {
    if (!obj) return 'N/A';
    try {
      return JSON.stringify(obj, null, 2);
    } catch (e) {
      return String(obj);
    }
  };

  if (loading && evaluations.length === 0) {
    return (
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Evaluations</h3>
        <div className="flex items-center justify-center py-8">
          <p className="text-gray-500">Loading evaluations...</p>
        </div>
      </div>
    );
  }

  if (error && evaluations.length === 0) {
    return (
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Evaluations</h3>
        <div className="p-4 bg-red-100 text-red-700 rounded mb-4">
          <p>{error}</p>
        </div>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-6 rounded-lg">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-700">Evaluations</h3>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">
            Showing {skip + 1}-{Math.min(skip + limit, total)} of {total}
          </span>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className={`px-3 py-1 rounded text-sm ${
              loading
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-yellow-100 text-yellow-800 rounded mb-4">
          <p className="text-sm">{error}</p>
        </div>
      )}

      {evaluations.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          <p>No evaluation results found for this clinic.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                    {/* Expand column - only for test_case_evaluation */}
                  </th>
                  <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Export
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {evaluations.map((evaluation) => {
                  const isExpanded = expandedRows.has(evaluation.id);
                  const isTestCase = evaluation.type === 'test_case_evaluation';
                  const detail = expandedDetails.get(evaluation.id);
                  const testCaseDetails = isExpanded && detail ? getTestCaseDetails(evaluation) : [];
                  const canExpand = isTestCase; // All test case evaluations can be expanded

                  return (
                    <React.Fragment key={evaluation.id}>
                      <tr className="hover:bg-gray-50">
                        {/* Expand/Collapse Icon Column */}
                        <td className="py-3 px-4 text-sm">
                          {canExpand ? (
                            <button
                              onClick={() => toggleRowExpansion(evaluation.id)}
                              className="text-gray-600 hover:text-gray-900 focus:outline-none"
                              title={isExpanded ? 'Collapse' : 'Expand'}
                            >
                              {isExpanded ? (
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                              )}
                            </button>
                          ) : (
                            <span className="w-5 h-5 inline-block"></span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900 font-mono">
                          {evaluation.id?.substring(0, 12)}...
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">
                          {evaluation.type || 'N/A'}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              evaluation.status === 'success'
                                ? 'bg-green-100 text-green-800'
                                : evaluation.status === 'error'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {evaluation.status || 'N/A'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500">
                          {formatTimestamp(evaluation.timestamp)}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <button
                            onClick={() => handleDownloadResult(evaluation.id)}
                            disabled={!canExport(evaluation)}
                            className={`px-3 py-1 rounded text-sm ${
                              canExport(evaluation)
                                ? 'bg-green-500 text-white hover:bg-green-600'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                            title={
                              canExport(evaluation)
                                ? 'Download Results as CSV (loads from database)'
                                : 'Export not available for this evaluation type'
                            }
                          >
                            Download Results
                          </button>
                        </td>
                      </tr>
                      {/* Expanded Row - Show Test Case Details */}
                      {isExpanded && canExpand && (
                        <tr>
                          <td colSpan="6" className="px-0 py-0 bg-gray-50">
                            <div className="px-6 py-4">
                              {loadingDetails.has(evaluation.id) ? (
                                <div className="text-center py-4">
                                  <p className="text-gray-500">Loading details...</p>
                                </div>
                              ) : testCaseDetails.length === 0 ? (
                                <div className="text-center py-4">
                                  <p className="text-gray-500">No test case details available</p>
                                </div>
                              ) : (
                                <>
                                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                                    Test Case Details
                                    {testCaseDetails.length > MAX_EXPANDED_RECORDS && (
                                      <span className="ml-2 text-xs text-orange-600 font-normal">
                                        (Showing first {MAX_EXPANDED_RECORDS} of {testCaseDetails.length} records. Please download for full data.)
                                      </span>
                                    )}
                                  </h4>
                                  <div className="overflow-x-auto max-h-[100rem] overflow-y-auto">
                                    <table className="min-w-full bg-white border border-gray-300 rounded">
                                      <thead className="bg-gray-200">
                                        <tr>
                                          <th className="py-2 px-3 border-b text-left text-xs font-medium text-gray-700 uppercase">
                                            Code
                                          </th>
                                          <th className="py-2 px-3 border-b text-left text-xs font-medium text-gray-700 uppercase">
                                            Question
                                          </th>
                                          <th className="py-2 px-3 border-b text-left text-xs font-medium text-gray-700 uppercase">
                                            Response
                                          </th>
                                          <th className="py-2 px-3 border-b text-left text-xs font-medium text-gray-700 uppercase">
                                            Tool Info
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-200">
                                        {testCaseDetails.slice(0, MAX_EXPANDED_RECORDS).map((detail, idx) => (
                                          <tr key={`${detail.code}-${idx}`} className="hover:bg-gray-50">
                                            <td className="py-2 px-3 text-xs text-gray-900 font-mono">
                                              {detail.code}
                                            </td>
                                            <td className="py-2 px-3 text-xs text-gray-700">
                                              {detail.question}
                                            </td>
                                            <td className="py-2 px-3 text-xs text-gray-700">
                                              {detail.response}
                                            </td>
                                            <td className="py-2 px-3 text-xs text-gray-700">
                                              {detail.tool_info || 'N/A'}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="mt-4 flex justify-between items-center">
              <button
                onClick={() => setSkip(Math.max(0, skip - limit))}
                disabled={skip === 0}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {Math.floor(skip / limit) + 1} of {Math.ceil(total / limit)} ({total} total)
              </span>
              <button
                onClick={() => setSkip(skip + limit)}
                disabled={skip + limit >= total}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EvaluationSection;

